import { NonRosterUser, RosterParticipant } from '../../../../models';

export type AdultParticipant = RosterParticipant | NonRosterUser;

export interface FamilyGroupsColumns {
  adultsOnRoster: RosterParticipant[];
  adultsNotOnRoster: NonRosterUser[];
  unassignedMinors: RosterParticipant[];
  missingDobCount: number;
}

export function isRosterAdult(participant: RosterParticipant): boolean {
  return participant.underEighteen === false;
}

export function isRosterMinor(participant: RosterParticipant): boolean {
  return participant.underEighteen === true;
}

export function isMissingDobRosterParticipant(participant: RosterParticipant): boolean {
  return participant.underEighteen === null;
}

export function isIneligibleParentAdult(participant: AdultParticipant): boolean {
  const { rolegl, rolelead } = participant.newtripUser;
  return rolegl === 1 || rolelead === 1;
}

export function parentIdsFromMinors(
  roster: RosterParticipant[],
  nonRosterUsers: NonRosterUser[] = [],
): Set<number> {
  const parentIds = new Set<number>();
  for (const participant of roster) {
    if (!isRosterMinor(participant)) {
      continue;
    }
    const parentId = participant.newtripUser.parentId;
    if (parentId !== null && parentId !== undefined && parentId !== 0) {
      const parent = findAdultParticipant(roster, nonRosterUsers, parentId);
      if (parent && isIneligibleParentAdult(parent)) {
        continue;
      }
      parentIds.add(parentId);
    }
  }
  return parentIds;
}

export function classifyFamilyGroupsColumns(
  roster: RosterParticipant[],
  nonRosterUsers: NonRosterUser[],
  familyGroupParentIds: ReadonlySet<number>,
): FamilyGroupsColumns {
  const classifiableRoster = roster.filter(
    (participant) => !isMissingDobRosterParticipant(participant),
  );
  const missingDobCount = roster.length - classifiableRoster.length;

  const adultsOnRoster = classifiableRoster.filter(
    (participant) =>
      isRosterAdult(participant) &&
      (!familyGroupParentIds.has(participant.id) ||
        isIneligibleParentAdult(participant)),
  );

  const adultsNotOnRoster = nonRosterUsers.filter(
    (participant) =>
      !familyGroupParentIds.has(participant.id) ||
      isIneligibleParentAdult(participant),
  );

  const unassignedMinors = classifiableRoster.filter((participant) => {
    if (!isRosterMinor(participant)) {
      return false;
    }
    const parentId = participant.newtripUser.parentId;
    if (parentId === null || parentId === undefined || parentId === 0) {
      return true;
    }
    const parent = findAdultParticipant(roster, nonRosterUsers, parentId);
    return parent === undefined || isIneligibleParentAdult(parent);
  });

  return {
    adultsOnRoster,
    adultsNotOnRoster,
    unassignedMinors,
    missingDobCount,
  };
}

export function findAdultParticipant(
  roster: RosterParticipant[],
  nonRosterUsers: NonRosterUser[],
  userId: number,
): AdultParticipant | undefined {
  return (
    roster.find((participant) => participant.id === userId) ??
    nonRosterUsers.find((participant) => participant.id === userId)
  );
}

export function isMinorDragData(data: unknown): data is RosterParticipant {
  if (!data || typeof data !== 'object' || !('type' in data)) {
    return false;
  }
  const participant = data as RosterParticipant;
  return participant.type === 'roster' && isRosterMinor(participant);
}

export function isAdultDragData(data: unknown): data is AdultParticipant {
  if (!data || typeof data !== 'object' || !('type' in data)) {
    return false;
  }
  const participant = data as AdultParticipant | RosterParticipant;
  if (participant.type === 'non-roster') {
    return !isIneligibleParentAdult(participant);
  }
  if (participant.type === 'roster') {
    return isRosterAdult(participant) && !isIneligibleParentAdult(participant);
  }
  return false;
}

export function minorsForParent(
  roster: RosterParticipant[],
  parentUserId: number,
): RosterParticipant[] {
  return roster.filter(
    (participant) =>
      isRosterMinor(participant) &&
      participant.newtripUser.parentId === parentUserId,
  );
}
